import { type } from 'arktype';

/**
 * Query schema for GET /api/v1/planning/shift-calendars/mine
 */
export const shiftCalendarQuerySchema = type({
  from: type('string').configure({
    description: 'Inclusive start of the requested calendar window as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-20'],
  }),
  to: type('string').configure({
    description: 'Inclusive end of the requested calendar window as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-26'],
  }),
}).configure({ description: 'Query parameters selecting the date window for the shift-calendar endpoint.' });

export type ShiftCalendarQuery = typeof shiftCalendarQuerySchema.t;

/**
 * A single shift entry rendered on the calendar for a specific date.
 */
export interface ShiftCalendarEntry {
  planInstanceId: string;
  shiftUnitId: string;
  shiftName: string;
  shiftIcon: string | null;
  shiftColor: string;
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  date: string; // YYYY-MM-DD
  /** Employee's primary location id (fallback when no override or template location). */
  locationId?: string | null;
  /** Location id configured on the shift unit template (null when unset). */
  shiftTemplateLocationId?: string | null;
  /** Human-readable name of the shift unit template location. */
  shiftTemplateLocationName?: string | null;
  /** Human-readable name of the EFFECTIVE location (override ?? template ?? primary). */
  locationName?: string | null;
  employeeOverridable?: boolean;
  overrideStartTime?: string;
  overrideEndTime?: string;
  /** Override location id when an employee has overridden it on this date. */
  overrideLocationId?: string | null;
  canEdit?: boolean;
  swapStatus?: 'looking_for_swap' | 'available_for_takeover' | null;
  /** Assignment provenance: concrete instance assignment or person default pattern. */
  source?: 'template' | 'override' | 'person_default';
}

/**
 * A plan containing shift entries for the current user or a team member.
 */
export interface ShiftCalendarPlan {
  planId: string;
  planName: string;
  teamId: string;
  teamName: string;
  teamColor: string | null;
  entries: ShiftCalendarEntry[];
}

/**
 * A team member with their shift entries.
 */
export interface ShiftCalendarMember {
  personId: string;
  personName: string;
  entries: ShiftCalendarEntry[];
}

/**
 * A team containing its members and their shift entries.
 */
export interface ShiftCalendarTeam {
  teamId: string;
  teamName: string;
  teamColor: string | null;
  teamIcon: string | null;
  teamImageUrl: string | null;
  members: ShiftCalendarMember[];
}

/**
 * A team badge — all visual + identifying fields needed to render a team
 * marker in the calendar sidebar. Sourced from the `teams` table.
 */
export interface ShiftCalendarTeamBadge {
  teamId: string;
  teamName: string;
  teamColor: string | null;
  teamIcon: string | null;
  teamImageUrl: string | null;
}

/**
 * Response for GET /api/v1/planning/shift-calendars/mine
 *
 * Groups shift assignments into:
 * - `myShifts`: Plans where the current user has assignments (one entry per plan).
 * - `teams`: Teams visible to the current user, each containing members with their shift entries.
 * - `memberTeamBadges`: For every person appearing under `teams[].members[]`, the
 *   full set of team-badge visuals for every team they belong to during the
 *   visible period (from `person_teams` joined with `teams`, filtered to
 *   memberships overlapping the requested date range). Lets the client render
 *   ALL of a member's active teams (not just the team the shift entry sits
 *   under).
 */
export interface ShiftCalendarsResponse {
  myShifts: ShiftCalendarPlan[];
  teams: ShiftCalendarTeam[];
  memberTeamBadges: Record<string, ShiftCalendarTeamBadge[]>;
}
